/**
 * RFC 162: bounded read operations.
 *
 * Runs a read with ONE total deadline (covering token wait + all pages), combined
 * with an optional caller signal. The operation receives a signal it must pass
 * to the transport. Timers/listeners are always cleaned up.
 *
 * Reads only — never wrap saves, deletes, payments, translations or reviews:
 * a write timeout has an ambiguous outcome.
 */
export const READ_DEADLINE_MS = 8000;

export class ReadDeadlineError extends Error {
  constructor(ms: number) {
    super(`Read did not complete within ${ms}ms`);
    this.name = 'ReadDeadlineError';
  }
}

export function isReadDeadlineError(error: unknown): error is ReadDeadlineError {
  return error instanceof ReadDeadlineError;
}

export async function withReadDeadline<T>(
  operation: (signal: AbortSignal) => PromiseLike<T>,
  { ms = READ_DEADLINE_MS, signal }: { ms?: number; signal?: AbortSignal } = {},
): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;

  const guard = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new ReadDeadlineError(ms));
    }, ms);
    if (signal) {
      onAbort = () => {
        controller.abort();
        reject(new DOMException('Read cancelled', 'AbortError'));
      };
      if (signal.aborted) onAbort();
      else signal.addEventListener('abort', onAbort, { once: true });
    }
  });

  try {
    // The outer race settles by the deadline even if the SDK is stuck on an
    // auth lock that ignores the abort signal; any late result is discarded.
    return await Promise.race([Promise.resolve(operation(controller.signal)), guard]);
  } finally {
    if (timer) clearTimeout(timer);
    if (signal && onAbort) signal.removeEventListener('abort', onAbort);
  }
}
